import React, { useState, useEffect } from 'react';
import { dataService, Registration } from '../services/dataService';
import { useApp } from '../context/AppContext';
import { DashboardHeader } from '../components';
import '../styles/dashboard.css';
import '../styles/dashboard-header.css';

// Animation keyframes as a style tag
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Interface for grouped student data
interface GroupedEvent {
  id: string;
  eventId: string;
  eventName: string;
  ageGroup: string;
  registrationDate: string;
}

interface GroupedStudent {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  schoolId: string;
  schoolName: string;
  events: GroupedEvent[];
}

const NewRegistrations: React.FC = () => {
  const { user } = useApp();
  const [pendingRegistrations, setPendingRegistrations] = useState<Registration[]>([]);
  const [groupedStudents, setGroupedStudents] = useState<GroupedStudent[]>([]);
  const [ageGroups, setAgeGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<GroupedStudent | null>(null);
  const [eventStatusSelections, setEventStatusSelections] = useState<{ [eventId: string]: 'approve' | 'reject' | 'pending' }>({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const registrations = await dataService.getRegistrations();
      const ageGroupsData = await dataService.getAgeGroups();
      
      // Filter registrations based on user role
      let filteredRegistrations = registrations;
      
      if (user?.role === 'headmaster' && user?.schoolId) {
        // Headmasters can only see registrations for their school
        filteredRegistrations = registrations.filter(reg => 
          reg.school_id === user.schoolId || reg.schoolId === user.schoolId
        );
      }
      
      // Filter pending registrations from the school-filtered results
      const pending = filteredRegistrations.filter(reg => reg.status === 'pending' || reg.status === null);
      
      setPendingRegistrations(pending);
      setAgeGroups(ageGroupsData);

      // Compute grouped students with proper school names
      const grouped = await groupRegistrationsByStudent(pending);
      setGroupedStudents(grouped);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEventName = (eventId: string) => {
    // Just return the sport field directly since that's where the event is stored
    return eventId || 'No Event';
  };

  const getAgeGroupName = (ageGroupValue: string) => {
    // If the value is already a name (from new database structure), return it directly
    if (ageGroupValue && (ageGroupValue.includes('Under') || ageGroupValue.includes('Over'))) {
      return ageGroupValue;
    }
    // Otherwise, try to find by ID (for backward compatibility)
    const ageGroup = ageGroups.find(ag => ag.id === ageGroupValue);
    return ageGroup ? ageGroup.name : ageGroupValue || 'Unknown Age Group';
  };

  // Group registrations by student (same name, DOB, and gender)
  const groupRegistrationsByStudent = async (registrations: Registration[]): Promise<GroupedStudent[]> => {
    const grouped = new Map<string, GroupedStudent>();

    registrations.forEach(registration => {
      // Create unique key using first_name, last_name, date_of_birth, and gender
      const studentKey = `${registration.first_name || registration.firstName || ''}_${registration.last_name || registration.lastName || ''}_${registration.date_of_birth || registration.dateOfBirth || ''}_${registration.gender || ''}`;
      
      if (grouped.has(studentKey)) {
        // Add event to existing student
        const existingStudent = grouped.get(studentKey);
        if (existingStudent) {
          existingStudent.events.push({
            id: registration.id || '',
            eventId: registration.sport || registration.eventId || '',
            eventName: getEventName(registration.sport || registration.eventId || ''),
            ageGroup: registration.age_group || registration.ageGroupId || '',
            registrationDate: registration.created_at?.split('T')[0] || registration.registrationDate || ''
          });
        }
      } else {
        // Create new student entry
        const schoolId = registration.school_id || registration.schoolId || '';
        
        grouped.set(studentKey, {
          firstName: registration.first_name || registration.firstName || '',
          lastName: registration.last_name || registration.lastName || '',
          dateOfBirth: registration.date_of_birth || registration.dateOfBirth || '',
          gender: registration.gender || '',
          schoolId: schoolId,
          schoolName: 'Loading...', // Temporary placeholder
          events: [{
            id: registration.id || '',
            eventId: registration.sport || registration.eventId || '',
            eventName: getEventName(registration.sport || registration.eventId || ''),
            ageGroup: registration.age_group || registration.ageGroupId || '',
            registrationDate: registration.created_at?.split('T')[0] || registration.registrationDate || ''
          }]
        });
      }
    });

    // Now fetch school names using getSchoolById like Reports.tsx does
    const studentsArray = Array.from(grouped.values());
    const studentsWithSchoolNames = await Promise.all(
      studentsArray.map(async (student) => {
        try {
          const schoolData = await dataService.getSchoolById(student.schoolId);
          return {
            ...student,
            schoolName: schoolData?.name || 'Unknown School'
          };
        } catch (error) {
          console.error('Error fetching school name for ID:', student.schoolId, error);
          return {
            ...student,
            schoolName: 'Unknown School'
          };
        }
      })
    );

    return studentsWithSchoolNames;
  };

  // Open edit dialog for a student
  const handleEditStudent = (student: GroupedStudent) => {
    setSelectedStudent(student);
    
    // Initialize status selections for each event as 'pending'
    const initialSelections: { [eventId: string]: 'approve' | 'reject' | 'pending' } = {};
    student.events.forEach(event => {
      initialSelections[event.id] = 'pending';
    });
    setEventStatusSelections(initialSelections);
    
    setShowEditDialog(true);
  };

  // Handle status selection change for an event
  const handleEventStatusSelection = (eventId: string, status: 'approve' | 'reject' | 'pending') => {
    setEventStatusSelections(prev => ({
      ...prev,
      [eventId]: status
    }));
  };

  // Handle bulk submission of all event status changes
  const handleBulkSubmit = async () => {
    if (!selectedStudent || !user?.id) return;
    
    try {
      // Process each event based on selected status
      for (const event of selectedStudent.events) {
        const selectedStatus = eventStatusSelections[event.id];
        
        if (selectedStatus === 'approve') {
          await dataService.updateRegistrationStatus(event.id, 'approved', user.id);
        } else if (selectedStatus === 'reject') {
          await dataService.updateRegistrationStatus(event.id, 'rejected', user.id);
        }
        // If 'pending', do nothing (keep current status)
      }
      
      // Reload data and close dialog
      await loadData();
      setShowEditDialog(false);
      setSelectedStudent(null);
      setEventStatusSelections({});
    } catch (error) {
      console.error('Failed to update registrations:', error);
      alert('Failed to update registrations. Please try again.');
    }
  };

  if (loading) {
    return (
      <>
        <style>{animationStyles}</style>
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #0A0E17 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '3px solid rgba(212, 175, 55, 0.2)',
            borderTop: '3px solid #D4AF37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            color: '#D4AF37',
            fontSize: '1.1rem',
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: '0.5px'
          }}>
            Loading registrations...
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{animationStyles}</style>
      <DashboardHeader />
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #0A0E17 100%)',
        padding: '2rem',
        paddingTop: '100px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(212, 175, 55, 0.2)',
        padding: '2rem',
        marginBottom: '2rem',
        animation: 'fadeInUp 0.6s ease-out forwards'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          marginBottom: '0.5rem'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A0E17" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #D4AF37 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '2px',
            margin: 0
          }}>
            {user?.role === 'headmaster' ? 'School Registrations' : 'New Registrations'}
          </h1>
        </div>
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.7)', 
          fontSize: '1rem',
          margin: 0,
          marginLeft: '66px',
          fontFamily: "'DM Sans', sans-serif"
        }}>
          {user?.role === 'headmaster'
            ? 'Review and approve or reject student registrations for your school'
            : 'View pending student registrations submitted to the system'
          }
        </p>
      </div>

      {/* Stats Card */}
      <div style={{ 
        background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        padding: '1.5rem 2rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1.5rem',
        animation: 'fadeInUp 0.6s ease-out 0.1s both'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: '#0A0E17',
          width: '70px',
          height: '70px',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.75rem',
          fontWeight: 'bold',
          fontFamily: "'Bebas Neue', sans-serif",
          boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
        }}>
          {pendingRegistrations.length}
        </div>
        <div>
          <h3 style={{ 
            margin: 0, 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            color: '#ffffff',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            {user?.role === 'headmaster' ? 'School Pending Registrations' : 'Pending Registrations'}
          </h3>
          <p style={{ 
            margin: 0, 
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.95rem',
            fontFamily: "'DM Sans', sans-serif",
            marginTop: '0.25rem'
          }}>
            {groupedStudents.length} students with {pendingRegistrations.length} event registrations
            {user?.role === 'headmaster' ? ' for your school' : ''}
          </p>
        </div>
      </div>

      {/* Registrations List */}
      {pendingRegistrations.length === 0 ? (
        <div style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          padding: '4rem 3rem',
          textAlign: 'center',
          animation: 'fadeInUp 0.6s ease-out 0.2s both'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          </div>
          <h3 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '700', 
            color: '#ffffff',
            marginBottom: '0.75rem',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '1px'
          }}>
            {user?.role === 'headmaster' ? 'No Pending School Registrations' : 'No Pending Registrations'}
          </h3>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.6)', 
            fontSize: '1.1rem',
            fontFamily: "'DM Sans', sans-serif",
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            {user?.role === 'headmaster' 
              ? 'All registrations for your school have been reviewed. Check back later for new submissions.'
              : 'All registrations have been reviewed. Check back later for new submissions.'
            }
          </p>
        </div>
      ) : (
        <div style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          overflow: 'hidden',
          animation: 'fadeInUp 0.6s ease-out 0.2s both'
        }}>
          {/* Table Header */}
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(212, 175, 55, 0.2)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: '600', 
              color: '#D4AF37', 
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="21" x2="9" y2="9"/>
              </svg>
              Registration Details
            </h3>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            {(() => {
              // Pagination logic
              const totalItems = groupedStudents.length;
              const totalPages = Math.ceil(totalItems / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const currentPageItems = groupedStudents.slice(startIndex, endIndex);

              return (
                <>
                  {/* Desktop Table */}
                  <div className="desktop-table">
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: '0.9rem'
                    }}>
                    <thead>
                      <tr style={{ 
                        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
                        borderBottom: '1px solid rgba(212, 175, 55, 0.2)'
                      }}>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          color: '#D4AF37',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          Student
                        </th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          color: '#D4AF37',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          Age Group
                        </th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          color: '#D4AF37',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          Events
                        </th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          color: '#D4AF37',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          Registration Date
                        </th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'center', 
                          fontWeight: '600',
                          color: '#D4AF37',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          {user?.role === 'headmaster' ? 'Actions' : 'Status'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPageItems.map((student, index) => (
                        <tr 
                          key={`${student.firstName}_${student.lastName}_${student.dateOfBirth}_${index}`}
                          style={{ 
                            borderBottom: index < currentPageItems.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                            transition: 'background-color 0.2s ease',
                            background: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                            <td style={{ 
                              padding: '1rem',
                              borderRight: '1px solid rgba(255, 255, 255, 0.05)'
                            }}>
                              <div>
                                <div style={{ 
                                  fontWeight: '600', 
                                  color: '#ffffff',
                                  marginBottom: '0.25rem',
                                  fontFamily: "'DM Sans', sans-serif"
                                }}>
                                  {student.firstName} {student.lastName}
                                </div>
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  color: 'rgba(255, 255, 255, 0.5)'
                                }}>
                                  {student.gender} • Born: {new Date(student.dateOfBirth).toLocaleDateString()}
                                </div>
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  color: 'rgba(255, 255, 255, 0.5)',
                                  marginTop: '0.25rem'
                                }}>
                                  {student.schoolName}
                                </div>
                              </div>
                            </td>
                            <td style={{ 
                              padding: '1rem',
                              borderRight: '1px solid rgba(255, 255, 255, 0.05)'
                            }}>
                              <span style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
                                color: '#60a5fa',
                                padding: '0.35rem 0.85rem',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                fontFamily: "'DM Sans', sans-serif"
                              }}>
                                {getAgeGroupName(student.events[0]?.ageGroup || '')}
                              </span>
                            </td>
                            <td style={{ 
                              padding: '1rem',
                              borderRight: '1px solid rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {student.events.map((event, eventIndex) => (
                                  <span 
                                    key={eventIndex}
                                    style={{
                                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                      color: '#34d399',
                                      padding: '0.25rem 0.65rem',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      fontWeight: '500',
                                      display: 'inline-block',
                                      border: '1px solid rgba(16, 185, 129, 0.3)',
                                      fontFamily: "'DM Sans', sans-serif"
                                    }}
                                  >
                                    {event.eventName}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td style={{ 
                              padding: '1rem',
                              borderRight: '1px solid rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.6)',
                              fontSize: '0.85rem',
                              fontFamily: "'DM Sans', sans-serif"
                            }}>
                              {new Date(student.events[0]?.registrationDate || '').toLocaleDateString()}
                            </td>
                            <td style={{ 
                              padding: '1rem',
                              textAlign: 'center'
                            }}>
                              {user?.role === 'headmaster' ? (
                                <button
                                  onClick={() => handleEditStudent(student)}
                                  style={{
                                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                                    color: '#0A0E17',
                                    border: 'none',
                                    padding: '0.5rem 1.25rem',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    margin: '0 auto',
                                    fontFamily: "'DM Sans', sans-serif"
                                  }}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                                    (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.5)';
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                                    (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 10px rgba(212, 175, 55, 0.3)';
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                  Edit
                                </button>
                              ) : (
                                <span style={{
                                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
                                  color: '#fbbf24',
                                  padding: '0.35rem 0.85rem',
                                  borderRadius: '8px',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  border: '1px solid rgba(245, 158, 11, 0.3)',
                                  fontFamily: "'DM Sans', sans-serif"
                                }}>
                                  Pending
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1.5rem 2rem',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      background: 'rgba(0, 0, 0, 0.2)'
                    }}>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontFamily: "'DM Sans', sans-serif"
                      }}>
                        Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center'
                      }}>
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            borderRadius: '8px',
                            background: currentPage === 1 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(212, 175, 55, 0.1)',
                            color: currentPage === 1 ? 'rgba(255, 255, 255, 0.3)' : '#D4AF37',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            fontFamily: "'DM Sans', sans-serif"
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage !== 1) {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(212, 175, 55, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentPage !== 1) {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(212, 175, 55, 0.1)';
                            }
                          }}
                        >
                          Previous
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              border: currentPage === pageNum ? '1px solid #D4AF37' : '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              background: currentPage === pageNum ? 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)' : 'transparent',
                              color: currentPage === pageNum ? '#0A0E17' : 'rgba(255, 255, 255, 0.7)',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: currentPage === pageNum ? '600' : '500',
                              minWidth: '40px',
                              transition: 'all 0.2s ease',
                              fontFamily: "'DM Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => {
                              if (currentPage !== pageNum) {
                                (e.currentTarget as HTMLElement).style.background = 'rgba(212, 175, 55, 0.15)';
                                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212, 175, 55, 0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (currentPage !== pageNum) {
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.2)';
                              }
                            }}
                          >
                            {pageNum}
                          </button>
                        ))}
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            borderRadius: '8px',
                            background: currentPage === totalPages ? 'rgba(255, 255, 255, 0.05)' : 'rgba(212, 175, 55, 0.1)',
                            color: currentPage === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#D4AF37',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            fontFamily: "'DM Sans', sans-serif"
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage !== totalPages) {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(212, 175, 55, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentPage !== totalPages) {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(212, 175, 55, 0.1)';
                            }
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  </div>

                  {/* Mobile Table */}
                  <div className="mobile-table" style={{ background: '#111827' }}>
                    <div className="dashboard-table-container" style={{ background: 'transparent' }}>
                      <table style={{ background: 'transparent' }}>
                        <thead>
                          <tr style={{ 
                            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
                            borderBottom: '1px solid rgba(212, 175, 55, 0.2)'
                          }}>
                            <th style={{ color: '#D4AF37', fontFamily: "'DM Sans', sans-serif" }}>Student Name</th>
                            <th style={{ color: '#D4AF37', fontFamily: "'DM Sans', sans-serif" }}>Events</th>
                            <th style={{ color: '#D4AF37', fontFamily: "'DM Sans', sans-serif" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentPageItems.map((student, index) => (
                            <tr 
                              key={`${student.firstName}_${student.lastName}_${student.dateOfBirth}_mobile_${index}`}
                              style={{
                                background: 'transparent',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}
                            >
                              <td style={{ color: '#ffffff' }}>
                                <div style={{ fontWeight: '600', color: '#ffffff', fontFamily: "'DM Sans', sans-serif" }}>
                                  {student.firstName} {student.lastName}
                                </div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                                  {student.gender} • {getAgeGroupName(student.events[0]?.ageGroup || '')}
                                </div>
                              </td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {student.events.map((event, eventIndex) => (
                                    <span 
                                      key={eventIndex} 
                                      style={{
                                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                        color: '#34d399',
                                        padding: '0.2rem 0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '0.7rem',
                                        fontWeight: '500',
                                        border: '1px solid rgba(16, 185, 129, 0.3)',
                                        display: 'inline-block'
                                      }}
                                    >
                                      {event.eventName}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                {user?.role === 'headmaster' ? (
                                  <button
                                    onClick={() => handleEditStudent(student)}
                                    style={{
                                      background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                                      color: '#0A0E17',
                                      border: 'none',
                                      padding: '0.4rem 0.8rem',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.3rem',
                                      fontFamily: "'DM Sans', sans-serif"
                                    }}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                      <path d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                    </svg>
                                    Edit
                                  </button>
                                ) : (
                                  <span style={{
                                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
                                    color: '#fbbf24',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    border: '1px solid rgba(245, 158, 11, 0.3)'
                                  }}>
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Edit Student Registration Dialog */}
      {showEditDialog && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: '16px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeInUp 0.3s ease-out forwards'
          }}>
            {/* Dialog Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '1px'
              }}>
                Edit Student Registration
              </h2>
              <button
                onClick={() => setShowEditDialog(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.7)',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                }}
              >
                ×
              </button>
            </div>

            {/* Dialog Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Student Information */}
              <div style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#D4AF37',
                  fontFamily: "'DM Sans', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Student Information
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  fontSize: '0.9rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontFamily: "'DM Sans', sans-serif"
                }}>
                  <div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Name:</span>{' '}
                    <span style={{ color: '#ffffff', fontWeight: '500' }}>{selectedStudent.firstName} {selectedStudent.lastName}</span>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Gender:</span>{' '}
                    <span style={{ color: '#ffffff', fontWeight: '500' }}>{selectedStudent.gender}</span>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Date of Birth:</span>{' '}
                    <span style={{ color: '#ffffff', fontWeight: '500' }}>{new Date(selectedStudent.dateOfBirth).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Age Group:</span>{' '}
                    <span style={{ color: '#ffffff', fontWeight: '500' }}>{getAgeGroupName(selectedStudent.events[0]?.ageGroup || '')}</span>
                  </div>
                </div>
              </div>

              {/* Registered Events */}
              <div>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#D4AF37',
                  fontFamily: "'DM Sans', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                  Registered Events
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {selectedStudent.events.map((event, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: '600',
                          color: '#ffffff',
                          marginBottom: '0.25rem',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          {event.eventName}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
                            color: '#fbbf24',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            letterSpacing: '0.5px'
                          }}>
                            PENDING
                          </span>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          color: 'rgba(255, 255, 255, 0.6)',
                          marginRight: '0.5rem',
                          fontFamily: "'DM Sans', sans-serif"
                        }}>
                          Action:
                        </span>
                        <select
                          value={eventStatusSelections[event.id] || 'pending'}
                          onChange={(e) => handleEventStatusSelection(event.id, e.target.value as 'approve' | 'reject' | 'pending')}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            background: '#1a1f2e',
                            color: '#ffffff',
                            fontSize: '0.85rem',
                            minWidth: '130px',
                            cursor: 'pointer',
                            fontFamily: "'DM Sans', sans-serif",
                            outline: 'none'
                          }}
                        >
                          <option value="pending" style={{ background: '#1a1f2e' }}>Keep Pending</option>
                          <option value="approve" style={{ background: '#1a1f2e' }}>Approve</option>
                          <option value="reject" style={{ background: '#1a1f2e' }}>Reject</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Submit and Cancel Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                marginTop: '2rem',
                paddingTop: '1.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <button
                  onClick={() => {
                    setShowEditDialog(false);
                    setSelectedStudent(null);
                    setEventStatusSelections({});
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: "'DM Sans', sans-serif"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkSubmit}
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                    color: '#0A0E17',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
                    fontFamily: "'DM Sans', sans-serif"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)';
                  }}
                >
                  Submit Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </>
  );
};

export default NewRegistrations;